import { Form, Modal, Table, Input, Button, message } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "../../utils/apiClient";

const isValidObjectId = (value) =>
  typeof value === "string" && /^[a-f\d]{24}$/i.test(value);

const Edit = ({
  isEditModalOpen,
  setIsEditModalOpen,
  categories,
  setCategories,
  products,
  setProducts,
  setCategoryTitle,
}) => {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const [editingRow, setEditingRow] = useState(null);
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    if (!editingRow) return;

    try {
      const oldTitle = editingRow.title;
      const newTitle = values.title;

      await apiRequest("/api/categories/update-category", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle, categoryId: editingRow._id }),
      });

      message.success(t("categoryUpdatedSuccess"));

      // Update categories state
      setCategories(
        categories.map((item) =>
          item._id === editingRow._id ? { ...item, title: newTitle } : item
        )
      );

      // Keep currently selected category focused on the renamed one
      if (setCategoryTitle) {
        setCategoryTitle(newTitle);
      }

      // Notify product list / other listeners to refresh and/or update local caches.
      window.dispatchEvent(
        new CustomEvent("categoryChanged", {
          detail: { type: "updated", oldTitle, newTitle, categoryId: editingRow._id },
        })
      );

      setEditingRow(null); // exit edit mode
      form.resetFields();
    } catch (error) {
      message.error(error?.message || t("somethingWentWrong"));
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm(t("deleteCategoryConfirm"))) return;

    if (!isValidObjectId(id)) {
      message.error(t("invalidCategoryIdRefreshing"));
      try {
        const fresh = await apiRequest("/api/categories/get-all");
        setCategories(Array.isArray(fresh) ? fresh : categories);
      } catch (e) {
        // ignore, error already shown below if needed
      }
      return;
    }

    try {
      await apiRequest("/api/categories/delete-category", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categoryId: id }),
      });

      message.success(t("categoryDeletedSuccess"));
      setCategories(categories.filter((item) => item._id !== id));

      // If user had this category selected elsewhere, listeners can reset to "All"
      window.dispatchEvent(
        new CustomEvent("categoryChanged", {
          detail: { type: "deleted", categoryId: id },
        })
      );
    } catch (error) {
      message.error(error?.message || t("somethingWentWrong"));
    }
  };

  const columns = [
    {
      title: t("categoryName"),
      dataIndex: "title",
      render: (_, record) => {
        if (editingRow?._id === record._id) {
          return (
            <Form.Item name="title" rules={[{ required: true, message: t("titleRequired") }]}>
              <Input placeholder={t("enterNewTitle")} />
            </Form.Item>
          );
        }
        return <p>{record.title}</p>;
      },
    },
    {
      title: t("action"),
      dataIndex: "action",
      render: (_, record) => (
        <div>
          <Button
            type="link"
            onClick={() => {
              setEditingRow(record);
              form.setFieldsValue({ title: record.title });
            }}
          >
            {t("editBtn")}
          </Button>
          {editingRow?._id === record._id && (
            <Button type="link" onClick={() => form.submit()}>
              {tCommon("save")}
            </Button>
          )}
          <Button type="text" danger onClick={() => deleteCategory(record._id)}>
            {t("delete")}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={t("categoryActions")}
      open={isEditModalOpen}
      onCancel={() => {
        setIsEditModalOpen(false);
        setEditingRow(null);
        form.resetFields();
      }}
      footer={false}
    >
      <Form form={form} onFinish={onFinish}>
        <Table bordered dataSource={categories} columns={columns} rowKey="_id" />
      </Form>
    </Modal>
  );
};

export default Edit;
